import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { VideoMoment } from "@/components/VideoMoment";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Send, Loader2, Volume2, BookMarked, MessageCircle, Trophy, GraduationCap, Sparkles, Paperclip } from "lucide-react";
import { useDownload } from "@/lib/libraryApi";
import { toast } from "sonner";
import api from "@/lib/api";
import { playAppAudio } from "@/lib/appAudio";
import { ttsOK } from "@/lib/languages";
import { useAuth } from "@/context/AuthContext";
import AppLayout from "@/components/AppLayout";
import { useI18n } from "@/context/I18nContext";
import Seo from "@/components/Seo";
import MissionComplete from "@/components/MissionComplete";
import { MissionAssets } from "@/components/assets/MissionAssets";

const STEP_KEYS = ["warm_up", "core_task", "extension"];

export default function MissionRunner() {
  const { t } = useI18n();
  const download = useDownload();
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [idx, setIdx] = useState(0);
  const [briefing, setBriefing] = useState(null); // 8.3.11: optional Video Moment opening (context only)
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/assignments/${assignmentId}`).then((r) => {
      setData(r.data);
      if (r.data.my_progress?.status === "completed") setDone(true);
      else if (r.data.mission?.video_moment_id) api.get(`/video-moments/${r.data.mission.video_moment_id}`).then((v) => setBriefing(v.data.moment)).catch(() => {});
    }).catch(() => setErr(true));
  }, [assignmentId]);

  const mission = data?.mission;
  const steps = (mission?.steps || []).filter((s) => !s.optional)
    .concat((mission?.steps || []).filter((s) => s.optional));
  const requiredCount = (mission?.steps || []).filter((s) => !s.optional).length;

  const saveProgress = (n) => api.post(`/assignments/${assignmentId}/progress`, { completed_steps: n }).catch(() => {});

  const next = () => {
    const n = Math.min(idx + 1, steps.length);
    if (n <= requiredCount) saveProgress(n);
    if (idx + 1 >= requiredCount && steps[idx]?.optional !== true && !steps[idx + 1]?.optional) {
      // reached end of required steps with no optional remaining -> allow finish on last
    }
    setIdx(n);
  };
  const prev = () => setIdx((i) => Math.max(0, i - 1));

  const finish = async () => {
    try {
      const r = await api.post(`/assignments/${assignmentId}/complete`);
      setData((d) => ({ ...d, savable_vocabulary: r.data.savable_vocabulary }));
      await refresh();
      setDone(true);
    } catch { toast.error(t("pm.mission.markFailed")); }
  };

  if (err) return <AppLayout><div className="max-w-2xl mx-auto px-4 py-10"><p className="text-coral">{t("missionRun.notAvailable")}</p><Link to="/assignments" className="underline">{t("missionRun.backToAssignments")}</Link></div></AppLayout>;
  if (!data) return <AppLayout><div className="max-w-2xl mx-auto px-4 py-10"><div className="nb-card h-48 animate-pulse" /></div></AppLayout>;

  if (done) return (
    <AppLayout>
      <Seo title={`${mission.title} · Mission complete`} noindex />
      <div className="max-w-xl mx-auto px-4 py-12">
        <MissionComplete assignmentId={assignmentId} classId={data.class_id} title={mission.title} savable={data.savable_vocabulary} />
      </div>
    </AppLayout>
  );

  const step = steps[idx];
  const onLastRequired = idx >= requiredCount - 1;

  return (
    <AppLayout>
      <Seo title={`${mission.title} · Mission`} noindex />
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Link to="/assignments" className="text-sm text-gray-600 flex items-center gap-1"><ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("missionRun.breadcrumb")}</Link>
        <div className="flex items-center gap-2 mt-2">
          <GraduationCap className="w-6 h-6 text-coral" />
          <h1 className="font-cabinet font-extrabold text-2xl" data-testid="mission-title-heading">{mission.title}</h1>
        </div>
        <p className="text-gray-600 text-sm">{data.class_name || t("missionRun.yourClass")} · {mission.target_language?.toUpperCase()} · {mission.level}</p>
        {(mission.attachments || []).length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2" data-testid="mission-attachments">
            {mission.attachments.map((f) => (
              <button key={f.resource_id} onClick={() => download(`/assignments/${assignmentId}/attachments/${f.resource_id}`, f.filename)} className="nb-tag bg-white text-xs normal-case tracking-normal" data-testid={`mission-attachment-${f.resource_id}`}>
                <Paperclip className="w-3 h-3 inline" /> <span dir="auto">{f.filename}</span>
              </button>
            ))}
          </div>
        )}

        <MissionAssets assignmentId={assignmentId} assets={mission.assets} />
        {briefing && <div className="mt-4"><VideoMoment moment={briefing} mode="briefing" onStart={() => setBriefing(null)} /></div>}
        {/* progress dots */}
        <div className="flex items-center gap-1.5 mt-4" data-testid="mission-steps">
          {steps.map((s, i) => (
            <div key={s.key} className={`h-2 flex-1 rounded-full border border-ink ${i < idx ? "bg-coral" : i === idx ? "bg-butter" : "bg-white"}`} />
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-1">{t("missionRun.stepOf", { current: Math.min(idx + 1, steps.length), total: steps.length })}{step?.optional ? ` · ${t("missionRun.optional")}` : ""}</p>

        <AnimatePresence mode="wait">
          <motion.div key={step?.key || idx} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} className="mt-5">
            {step?.key === "vocab" ? (
              <VocabStep mission={mission} />
            ) : step?.type === "conversation" ? (
              <ConversationStep mission={mission} user={user} assignmentId={assignmentId} canSave={data.can_save_vocab !== false} />
            ) : (
              <div className="nb-card p-6">
                <div className="nb-tag bg-lavender mb-3">{STEP_KEYS.includes(step?.key) ? t(`pm.mission.steps.${step.key}`) : step?.title}</div>
                <p className="text-lg leading-relaxed whitespace-pre-line" dir="auto">{step?.instructions}</p>
                {step?.key === "warm_up" && mission.student_instructions && (
                  <p className="text-sm text-gray-600 mt-4 italic" dir="auto">{mission.student_instructions}</p>
                )}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between gap-3 mt-6">
          <button onClick={prev} disabled={idx === 0} className="nb-btn-outline px-4 py-3 disabled:opacity-40" data-testid="mission-prev"><ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {t("missionRun.back")}</button>
          {idx < steps.length - 1 ? (
            <button onClick={next} className="nb-btn-primary px-6 py-3 flex-1" data-testid="mission-next">
              {onLastRequired ? t("missionRun.continueBonus") : t("missionRun.nextStep")} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
            </button>
          ) : (
            <button onClick={finish} className="nb-btn-mint px-6 py-3 flex-1" data-testid="mission-finish"><Trophy className="w-5 h-5" /> {t("missionRun.completeMission")}</button>
          )}
        </div>
        {onLastRequired && idx < steps.length - 1 && (
          <button onClick={finish} className="w-full text-sm text-gray-500 mt-3 underline" data-testid="mission-finish-early">{t("missionRun.finishEarly")}</button>
        )}
      </div>
    </AppLayout>
  );
}

function VocabStep({ mission }) {
  const { t } = useI18n();
  return (
    <div className="nb-card p-6">
      <div className="nb-tag bg-mint mb-3 flex items-center gap-1 w-fit"><BookMarked className="w-4 h-4" /> {t("pm.mission.keyWords")}</div>
      <div className="space-y-2">
        {(mission.target_vocabulary || []).map((v, i) => (
          <div key={`${v.term}-${i}`} className="flex items-center justify-between border-2 border-ink rounded-xl px-3 py-2 bg-mint/30">
            <div><span className="font-cabinet font-bold" dir="auto">{v.term}</span> <span className="text-sm text-gray-600" dir="auto">— {v.translation}</span></div>
          </div>
        ))}
      </div>
      {mission.useful_phrases?.length > 0 && (
        <>
          <div className="nb-tag bg-peach mt-5 mb-2 w-fit">{t("mission.usefulPhrases")}</div>
          <div className="space-y-2">
            {mission.useful_phrases.map((p, i) => (
              <div key={`${p.phrase}-${i}`} className="border-2 border-ink rounded-xl px-3 py-2 bg-peach/30">
                <div className="font-bold" dir="auto">{p.phrase}</div><div className="text-xs text-gray-600" dir="auto">{p.translation}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Missions are text-only by design: no mic, no spoken replies. Only saved Vocab cards get a safe Listen.
function ConversationStep({ mission, user, assignmentId, canSave }) {
  const { t } = useI18n();
  const [messages, setMessages] = useState([]);
  const [corrections, setCorrections] = useState([]);
  const [learned, setLearned] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showRecap, setShowRecap] = useState(false);
  const [savedWords, setSavedWords] = useState({});
  const endRef = useRef(null);
  const convSession = useRef(`cs${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`);
  const lang = mission.target_language || user?.target_lang;

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);

  const persona = `a friendly local in this situation: ${mission.scenario}`;
  const scenario = mission.conversation_challenge;

  const sendMessage = async (text) => {
    if (!text.trim() || loading || !lang) return;
    const msg = text.trim();
    const hist = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((m) => [...m, { id: Date.now(), role: "user", content: msg }]);
    setLoading(true);
    try {
      const r = await api.post("/engine/process", {
        mode: "converse", target_lang: lang,
        native_lang: mission.native_language || user?.native_lang || "en",
        level: mission.level || "A1", bundle: "roommate",
        payload: { persona, scenario, message: msg, history: hist,
                   assignment_id: assignmentId, interaction_mode: "text",
                   defer_vocab: true, conversation_session_id: convSession.current },
      });
      setMessages((m) => [...m, { id: Date.now() + 1, role: "assistant", content: r.data.reply, translation: r.data.reply_translation }]);
      if (r.data.corrections?.length) setCorrections((c) => [...r.data.corrections, ...c]);
      if (r.data.new_vocab?.length) setLearned((v) => [...r.data.new_vocab, ...v]);
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || t("pm.mission.messageFailed"));
      setMessages((m) => m.slice(0, -1));
    } finally { setLoading(false); }
  };

  const addWords = async (items) => {
    const fresh = items.filter((v) => v.word && !savedWords[v.word]);
    if (!fresh.length) return;
    try {
      // Saves into the learner's own profile for the MISSION's language (never another language).
      const r = await api.post(`/assignments/${assignmentId}/save-vocabulary`, {
        items: fresh.slice(0, 10).map((v) => ({ word: v.word, translation: v.translation || "", example_sentence: v.example_sentence || "" })),
      });
      const ids = Object.fromEntries((r.data?.saved || []).map((x) => [x.word, x.id]));
      setSavedWords((s) => { const n = { ...s }; fresh.forEach((v) => { n[v.word] = ids[v.word] || true; }); return n; });
      toast.success(t("pm.mission.added", { count: r.data?.added ?? fresh.length }));
    } catch (e) { toast.error(e?.response?.status === 409 ? t("missionRun.cantSave") : t("pm.mission.addFailed")); }
  };
  const listen = (id) => playAppAudio(`/vocab/${id}/audio`).catch(() => toast.error(t("pm.common.voiceUnavailable")));

  return (
    <div className="nb-card p-0 flex flex-col h-[70vh] sm:h-[56vh] overflow-hidden" data-testid="mission-conversation">
      <div className="border-b-2 border-ink p-3 bg-sky">
        <div className="nb-tag bg-white flex items-center gap-1 w-fit"><MessageCircle className="w-4 h-4" /> {t("pm.mission.challenge")}</div>
        <p className="text-sm mt-2" dir="auto">{mission.conversation_challenge}</p>
      </div>

      <div className="flex-1 overflow-auto p-4 space-y-3 no-scrollbar" data-testid="mission-chat">
        {messages.length === 0 && <p className="text-center text-gray-400 text-sm mt-6">{t("mission.typeFirst")}</p>}
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] border-2 border-ink rounded-2xl px-3 py-2 ${m.role === "user" ? "bg-lavender rounded-se-none" : "bg-gray-100 rounded-ss-none"}`}>
              <p className="font-medium" dir="auto">{m.content}</p>
              {m.translation && <p className="text-xs text-gray-500 mt-1" dir="auto">{m.translation}</p>}
            </div>
          </div>
        ))}
        {loading && <div className="text-sm text-gray-400">…</div>}
        <div ref={endRef} />
      </div>

      <div className="border-t-2 border-ink p-3 flex gap-2">
        <input className="nb-input flex-1" placeholder={t("mission.typeReply")} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { sendMessage(input); setInput(""); } }} data-testid="mission-chat-input" />
        <button onClick={() => { sendMessage(input); setInput(""); }} disabled={loading} className="nb-btn-primary px-4" data-testid="mission-chat-send">{loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}</button>
      </div>

      {/* Learning recap (derived from this conversation — no extra AI call) */}
      {messages.some((m) => m.role === "assistant") && (
        <div className="border-t-2 border-ink max-h-[35%] overflow-auto shrink-0" data-testid="recap-region">
          <button onClick={() => setShowRecap((v) => !v)} className="w-full text-start px-4 py-2 text-sm font-bold flex items-center gap-2" data-testid="recap-toggle"><Sparkles className="w-4 h-4 text-coral" /> {showRecap ? t("mission.hideRecap") : t("mission.showRecap")} {t("mission.recap")}</button>
          {showRecap && (
            <div className="px-4 pb-4 grid sm:grid-cols-3 gap-3" data-testid="conversation-recap">
              <RecapCard title={t("mission.usefulPhrases")} tone="bg-peach/40">
                {(mission.useful_phrases || []).slice(0, 4).map((p, i) => <li key={`${p.phrase}-${i}`} dir="auto"><b dir="auto">{p.phrase}</b> — <span className="text-gray-600" dir="auto">{p.translation}</span></li>)}
                {!(mission.useful_phrases || []).length && <li className="text-gray-400 list-none">{t("pm.mission.fromBrief")}</li>}
              </RecapCard>
              <div className="border-2 border-ink rounded-xl p-3 bg-mint/40" data-testid="recap-words">
                <div className="flex items-center justify-between mb-1">
                  <div className="font-cabinet font-bold text-sm">{t("mission.wordsToRemember")}</div>
                  {canSave && learned.some((v) => !savedWords[v.word]) && (
                    <button onClick={() => addWords(learned)} className="text-xs text-coral font-bold" data-testid="recap-add-all">{t("common.addAll")}</button>
                  )}
                </div>
                <ul className="text-sm space-y-1">
                  {learned.slice(0, 6).map((v, i) => (
                    <li key={`${v.word}-${i}`} className="flex items-center justify-between gap-2">
                      <span><b dir="auto">{v.word}</b> — <span className="text-gray-600" dir="auto">{v.translation}</span></span>
                      {savedWords[v.word]
                        ? <span className="text-xs text-gray-400 flex items-center gap-1">
                            {typeof savedWords[v.word] === "string" && ttsOK(lang) && <button onClick={() => listen(savedWords[v.word])} className="text-coral" data-testid={`recap-listen-${i}`} aria-label={t("pm.mission.listen")}><Volume2 className="w-3.5 h-3.5" /></button>}
                            <Check className="w-3 h-3" /> {t("common.added")}
                          </span>
                        : canSave && <button onClick={() => addWords([v])} className="text-xs font-bold text-coral shrink-0" data-testid={`recap-add-${i}`}>+ {t("common.add")}</button>}
                    </li>
                  ))}
                  {!learned.length && <li className="text-gray-400">{t("pm.mission.newWordsHint")}</li>}
                  {!canSave && learned.length > 0 && <li className="text-xs text-gray-600" data-testid="recap-cant-save">{t("missionRun.cantSave")}</li>}
                </ul>
              </div>
              <RecapCard title={t("today.practiceAgain")} tone="bg-lavender/40">
                {corrections.slice(0, 4).map((c, i) => <li key={`${c.original}-${i}`}><span className="line-through text-gray-500" dir="auto">{c.original}</span> <span className="inline-block rtl:rotate-180">→</span> <b dir="auto">{c.corrected}</b></li>)}
                {!corrections.length && <li className="text-gray-400 list-none">{t("pm.mission.noCorrections")}</li>}
              </RecapCard>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RecapCard({ title, tone, children }) {
  return (
    <div className={`border-2 border-ink rounded-xl p-3 ${tone}`}>
      <div className="font-cabinet font-bold text-sm mb-1">{title}</div>
      <ul className="text-sm space-y-1 list-disc list-inside">{children}</ul>
    </div>
  );
}
